import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";

/**
 * Batch-fetches pending-approval metadata for the visible voucher rows on a
 * list page. Returns a map: `{ [document_id]: { request_id, current_level,
 * final_level, role_at_level, users_at_level, raised_at, days_pending,
 * can_act, status } }`.
 *
 * Re-runs whenever the joined set of `ids` changes (so adding/removing rows
 * via pagination/search triggers a refetch). Uses a `cancelled` flag captured
 * by the effect closure to discard stale responses safely.
 *
 * Usage:
 *   const ids = useMemo(() => list.filter(v => v.status === "Pending").map(v => v.id), [list]);
 *   const pendingMap = useApprovalPendingMap("purchase_invoice", ids);
 */
export function useApprovalPendingMap(module, ids) {
  const [map, setMap] = useState({});
  const key = useMemo(() => (ids || []).slice().sort().join("|"), [ids]);

  useEffect(() => {
    if (!module || !ids || ids.length === 0) {
      setMap({});
      return undefined;
    }
    let cancelled = false;
    api
      .post("/approvals/pending-status", { module, document_ids: ids })
      .then((r) => {
        if (cancelled) return;
        setMap(r.data || {});
      })
      .catch(() => {
        if (cancelled) return;
        setMap({});
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module, key]);

  return map;
}
